import StorySource from '../../data/story-source';
import LocalStorage from '../../data/local-storage';
import CONFIG from '../../config/app-config';
import AuthService from '../../services/auth-service';
import BookmarkService from '../../services/bookmark-service';
import {
  getLocationName as fetchLocationName,
  getUserLocation as detectUserLocation,
  getMapStyleUrl,
  buildMapConfig
} from '../../services/location-service';
import { calculateDistance as distanceCalc } from '../../utils/helpers';

export default class NearbyModel {
  constructor() {
    this.nearbyStories = [];
    this.currentUserLocation = null;
    this.nearbyDistance = 500;
    this.currentMapLayer = 'streets';
    this.isAuthenticated = false;
    this.user = {};
    this.bookmarks = [];
  }

  loadUserState() {
    const { isAuthenticated, user } = AuthService;
    this.isAuthenticated = isAuthenticated;
    this.user = user || {};
    this.bookmarks = BookmarkService.getBookmarks();

    if (isAuthenticated && user.id) {
      this.currentUserLocation = LocalStorage.getUserSpecificObject(
        'heliaedu_user_location',
        user.id
      );
      this.nearbyDistance = parseInt(
        LocalStorage.getOrSetUserSpecificItem('heliaedu_user_distance', user.id, '500')
      );
      this.currentMapLayer = LocalStorage.getOrSetUserSpecificItem(
        'heliaedu_user_map_layer',
        user.id,
        'streets'
      );
    } else {
      this.currentUserLocation = null;
      this.nearbyDistance = parseInt(
        LocalStorage.getOrSetItem('heliaedu_guest_distance', '500')
      );
      this.currentMapLayer = LocalStorage.getOrSetItem('heliaedu_guest_map_layer', 'streets');
    }
  }

  async getAllStoriesWithLocation() {
    const response = await StorySource.getAllStories({
      location: 1, // kalau 1 true kalau 0 false (bool sus)
      size: 100,
    });
    if (!response.error) {
      return response.listStory || [];
    }
    throw new Error(response.message || 'Failed to fetch stories');
  }

  async getAllNearbyStories() {
    try {
      // Fetch dengan size lebih besar untuk nearby page
      const response = await StorySource.getAllStories({
        location: 1, // kalau 1 true kalau 0 false (bool sus)
        size: 200,
      });

      if (!response.error && response.listStory) {
        return response.listStory.filter((story) => story.lat && story.lon);
      }
      return [];
    } catch (error) {
      throw new Error('Failed to fetch nearby stories: ' + error.message);
    }
  }

  async getUserLocation() {
    return detectUserLocation();
  }

  calculateDistance(lat1, lon1, lat2, lon2) {
    return distanceCalc(lat1, lon1, lat2, lon2);
  }

  filterStoriesByDistance(stories) {
    if (!this.currentUserLocation) {
      return stories.filter((story) => story.lat && story.lon);
    }

    return stories.filter((story) => {
      if (!story.lat || !story.lon) return false;

      const distance = this.calculateDistance(
        this.currentUserLocation.lat,
        this.currentUserLocation.lon,
        story.lat,
        story.lon
      );
      return distance <= this.nearbyDistance;
    });
  }

  async getLocationName(lat, lon) {
    return fetchLocationName(lat, lon);
  }

  getMapStyleConfig(isDark = false, layerType = this.currentMapLayer || 'streets') {
    return getMapStyleUrl(isDark, layerType);
  }

  getMapConfig(isDark = false) {
    return buildMapConfig('nearby-map', isDark, this.currentMapLayer);
  }

  getMapLayerKey() {
    return this.isAuthenticated && this.user.id
      ? LocalStorage.getUserSpecificKey('heliaedu_user_map_layer', this.user.id)
      : 'heliaedu_guest_map_layer';
  }

  setCurrentMapLayer(layerType) {
    this.currentMapLayer = layerType;
    LocalStorage.setItem(this.getMapLayerKey(), layerType);
  }

  getCurrentMapLayer() {
    return this.currentMapLayer;
  }

  getRangeCircleData(radiusInKm) {
    if (!this.currentUserLocation) return null;

    const center = [this.currentUserLocation.lon, this.currentUserLocation.lat];
    const points = 64;
    const coords = [];

    for (let i = 0; i < points; i++) {
      const angle = (i / points) * 2 * Math.PI;
      const dx =
        (radiusInKm * Math.cos(angle)) /
        (111.32 * Math.cos((center[1] * Math.PI) / 180));
      const dy = (radiusInKm * Math.sin(angle)) / 110.574;
      coords.push([center[0] + dx, center[1] + dy]);
    }
    coords.push(coords[0]);

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [coords],
      },
    };
  }

  setNearbyStories(stories) {
    this.nearbyStories = stories;
  }

  setCurrentUserLocation(location) {
    this.currentUserLocation = location;

    if (this.isAuthenticated && this.user.id) {
      LocalStorage.setUserSpecificObject(
        'heliaedu_user_location',
        this.user.id,
        location
      );
    }
  }

  updateDistance(newDistance) {
    this.nearbyDistance = newDistance;

    if (this.isAuthenticated && this.user.id) {
      LocalStorage.setUserSpecificItem(
        'heliaedu_user_distance',
        this.user.id,
        this.nearbyDistance.toString()
      );
    } else {
      LocalStorage.setItem('heliaedu_guest_distance', this.nearbyDistance.toString());
    }
  }

  async loadNearbyUsersData(currentUserLocation, nearbyDistance) {
    const response = await StorySource.getAllStories({ location: 1, size: 100 });

    if (!response.error && response.listStory) {
      const storiesWithLocation = response.listStory.filter((story) => story.lat && story.lon);

      let nearbyStoriesForUsers = storiesWithLocation;
      if (currentUserLocation) {
        nearbyStoriesForUsers = storiesWithLocation.filter((story) => {
          const distance = this.calculateDistance(
            currentUserLocation.lat,
            currentUserLocation.lon,
            story.lat,
            story.lon
          );
          return distance <= nearbyDistance;
        });
      }

      return nearbyStoriesForUsers.slice(0, 3);
    }

    throw new Error('Failed to load nearby users');
  }

  isBookmarked(storyId) {
    return BookmarkService.getBookmarks().some((b) => b.storyId === storyId);
  }

  addBookmark(storyId) {
    const story = this.nearbyStories.find((s) => s.id === storyId);

    if (story && !this.isBookmarked(storyId)) {
      const bookmark = BookmarkService.addBookmark(story);
      this.bookmarks = BookmarkService.getBookmarks();
      return bookmark;
    }
    return null;
  }

  removeBookmark(storyId) {
    const count = BookmarkService.removeBookmark(storyId);
    this.bookmarks = BookmarkService.getBookmarks();
    return count > 0;
  }
}
